/**
 * Team Workload - dates.
 *
 * Every date here is a local `YYYY-MM-DD` string. Strings compare correctly as text, which keeps the range checks free of time
 * zones; a `Date` is only built to step from one day to the next.
 */

export function toISO(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Parsed at local midnight - `new Date('YYYY-MM-DD')` would be UTC and drift a day. */
export function fromISO(iso) {
  return new Date(`${iso}T00:00:00`);
}

export function todayISO() {
  return toISO(new Date());
}

export function addDays(iso, days) {
  const date = fromISO(iso);
  date.setDate(date.getDate() + days);
  return toISO(date);
}

export function addMonths(iso, months) {
  const date = fromISO(iso);
  date.setDate(1);
  date.setMonth(date.getMonth() + months);
  return toISO(date);
}

/** Monday of the week the date sits in. */
export function startOfWeek(iso) {
  const date = fromISO(iso);
  const shift = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - shift);
  return toISO(date);
}

export function startOfMonth(iso) {
  const date = fromISO(iso);
  date.setDate(1);
  return toISO(date);
}

export function endOfMonth(iso) {
  const date = fromISO(iso);
  date.setMonth(date.getMonth() + 1, 0);
  return toISO(date);
}

/** Every day from `from` to `to`, inclusive. Capped so a typo cannot hang the tab. */
export function daysBetween(from, to, cap = 400) {
  const out = [];
  let day = from;
  while (day <= to && out.length < cap) {
    out.push(day);
    day = addDays(day, 1);
  }
  return out;
}

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const dayDiff = (from, to) => Math.round((fromISO(to) - fromISO(from)) / 86_400_000);

/** The range a preset means, anchored on a day. */
export function rangeFor(preset, anchor) {
  if (preset === 'day') return { from: anchor, to: anchor };
  if (preset === 'week') {
    const from = startOfWeek(anchor);
    return { from, to: addDays(from, 6) };
  }
  return { from: startOfMonth(anchor), to: endOfMonth(anchor) };
}

/** One preset step back or forward. */
export function shiftAnchor(preset, anchor, direction) {
  if (preset === 'day') return addDays(anchor, direction);
  if (preset === 'week') return addDays(anchor, 7 * direction);
  return addMonths(anchor, direction);
}

/** The Monday-to-Sunday grid that holds a month, padded with the neighbouring days. */
export function monthGrid(anchor) {
  const start = startOfWeek(startOfMonth(anchor));
  const end = addDays(startOfWeek(endOfMonth(anchor)), 6);
  return daysBetween(start, end);
}

/* ── Formatting ─────────────────────────────────────────────────────────── */

const DAY_FMT = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
const SHORT_FMT = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short' });
const LONG_FMT = new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const MONTH_FMT = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' });
const WEEKDAY_FMT = new Intl.DateTimeFormat('en-GB', { weekday: 'short' });

export const formatDay = (iso) => (iso ? DAY_FMT.format(fromISO(iso)) : '-');
export const formatShort = (iso) => (iso ? SHORT_FMT.format(fromISO(iso)) : '-');
export const formatLong = (iso) => (iso ? LONG_FMT.format(fromISO(iso)) : '-');
export const formatMonth = (iso) => (iso ? MONTH_FMT.format(fromISO(iso)) : '-');
export const formatWeekday = (iso) => (iso ? WEEKDAY_FMT.format(fromISO(iso)) : '');

/** "Tuesday, 6 October 2026" for a single day, "October 2026" for a whole month, "6 Oct – 12 Oct 2026" otherwise. */
export function describeRange(from, to) {
  if (from === to) return formatLong(from);
  if (from === startOfMonth(from) && to === endOfMonth(from)) return formatMonth(from);
  return `${formatShort(from)} – ${formatDay(to)}`;
}

/** "2d over", "today", "in 2d", "12 Oct" - and the tone that goes with it. */
export function dueText(due, today) {
  if (!due) return { text: null, tone: 'none' };
  const left = dayDiff(today, due);
  if (left < 0) return { text: `${Math.abs(left)}d over`, tone: 'overdue' };
  if (left === 0) return { text: 'today', tone: 'soon' };
  if (left <= 3) return { text: `in ${left}d`, tone: 'soon' };
  return { text: formatShort(due), tone: 'ok' };
}
